// assets/facets-popup.js
// Keeps the facets popup open while selecting filter options.
// Applies filters only when pressing the "See X items" button.
// Closes only on outside click or Escape.
// Persists open state across Shopify section rerenders.

(function () {
  const STORAGE_PREFIX = "facetsPopupOpen:";
  const BODY_OPEN_CLASS = "facets-popup-open";
  const COUNT_DEBOUNCE_MS = 220;

  function getSectionId(el) {
    const section = el.closest("section[id]");
    return section ? section.id : "global";
  }

  function lockBody() {
    document.documentElement.classList.add(BODY_OPEN_CLASS);
    document.body.classList.add(BODY_OPEN_CLASS);
  }

  function unlockBody() {
    document.documentElement.classList.remove(BODY_OPEN_CLASS);
    document.body.classList.remove(BODY_OPEN_CLASS);
  }

  function initFacetsPopup(wrapper) {
    if (!wrapper || wrapper.dataset.facetsPopupInit === "1") return;

    const trigger = wrapper.querySelector("[data-facets-popup-trigger]");
    const panel = wrapper.querySelector("[data-facets-popup-panel]");
    if (!trigger || !panel) return;

    wrapper.dataset.facetsPopupInit = "1";

    // Store cleanup handlers for memory leak prevention
    const cleanupHandlers = [];

    const storageKey = STORAGE_PREFIX + getSectionId(wrapper);
    let updateCountTimeout = null;
    let updateCountRequestId = 0;

    const isOpen = () => wrapper.classList.contains("is-open");

    const adjustPopupPosition = () => {
      if (!panel) return;

      // Reset any previous positioning adjustments
      panel.style.removeProperty('top');
      panel.style.removeProperty('bottom');
      panel.style.removeProperty('max-height');

      // Wait for next frame to get accurate measurements
      requestAnimationFrame(() => {
        const triggerRect = trigger.getBoundingClientRect();
        const panelRect = panel.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;
        const panelHeight = panelRect.height;

        // Add buffer for better UX (80px for footer/spacing)
        const minSpaceNeeded = Math.min(panelHeight, 400);
        const footerBuffer = 80;

        // Check if there's enough space below
        if (spaceBelow < minSpaceNeeded + footerBuffer && spaceAbove > spaceBelow) {
          // Not enough space below, but more space above - position above trigger
          panel.style.top = 'auto';
          panel.style.bottom = `calc(100% + 10px)`;
          panel.style.maxHeight = `${Math.min(spaceAbove - 20, 60 * window.innerHeight / 100)}px`;
        } else if (spaceBelow < minSpaceNeeded + footerBuffer) {
          // Limited space both above and below - constrain height
          panel.style.maxHeight = `${spaceBelow - footerBuffer}px`;
        }
      });
    };

    const open = () => {
      wrapper.classList.add("is-open");
      trigger.setAttribute("aria-expanded", "true");
      lockBody();
      try {
        sessionStorage.setItem(storageKey, "1");
      } catch (e) {}

      // Adjust position to avoid footer overlap
      adjustPopupPosition();
    };

    const close = () => {
      wrapper.classList.remove("is-open");
      trigger.setAttribute("aria-expanded", "false");
      unlockBody();
      try {
        sessionStorage.removeItem(storageKey);
      } catch (e) {}
    };

    const toggle = () => {
      if (isOpen()) close();
      else {
        open();
        scheduleApplyCountUpdate();
      }
    };

    const setApplyButtonLabel = (count) => {
      const applyBtn = panel.querySelector("[data-facets-apply]");
      if (!(applyBtn instanceof HTMLButtonElement)) return;

      const base = applyBtn.dataset.applyBaseText || "Apply filters";
      const safeCount = Number.isFinite(Number(count)) ? Number(count) : 0;
      applyBtn.dataset.productsCount = String(safeCount);
      applyBtn.classList.remove("is-loading");
      applyBtn.textContent = `${base} (${safeCount})`;
    };

    const setApplyButtonLoading = () => {
      const applyBtn = panel.querySelector("[data-facets-apply]");
      if (!(applyBtn instanceof HTMLButtonElement)) return;
      if (applyBtn.classList.contains("is-loading")) return;

      const base = applyBtn.dataset.applyBaseText || "Apply filters";
      applyBtn.classList.add("is-loading");
      applyBtn.innerHTML = "";
      applyBtn.appendChild(document.createTextNode(`${base} `));
      const dots = document.createElement("span");
      dots.className = "facets__loading-dots";
      dots.setAttribute("aria-hidden", "true");
      dots.appendChild(document.createElement("span"));
      dots.appendChild(document.createElement("span"));
      dots.appendChild(document.createElement("span"));
      applyBtn.appendChild(dots);
    };

    const getSelectedCollectionUrl = () => {
      const selectedCollectionInput = panel.querySelector('input[name="collection-filter"]:checked');
      if (!(selectedCollectionInput instanceof HTMLInputElement)) return '';
      const item = selectedCollectionInput.closest('li');
      const urlInput = item?.querySelector('[data-collection-url]');
      return urlInput instanceof HTMLInputElement ? urlInput.value : '';
    };

    const appendParamsToUrl = (baseUrl, params) => {
      const nextUrl = new URL(baseUrl, window.location.origin);
      nextUrl.searchParams.delete('collection-filter');
      nextUrl.searchParams.delete('collection_redirect_url');

      for (const [key, value] of params.entries()) {
        if (key === 'collection-filter' || key === 'collection_redirect_url') continue;
        nextUrl.searchParams.append(key, value);
      }
      return nextUrl;
    };

    const updateApplyCount = async () => {
      const facetsForm =
        panel.closest("facets-form-component") || wrapper.closest("facets-form-component") || panel.querySelector("facets-form-component");
      if (!facetsForm) return;
      if (typeof facetsForm.createURLParameters !== "function") return;

      const sectionId = facetsForm.getAttribute("section-id");
      if (!sectionId) return;

      const currentRequestId = ++updateCountRequestId;

      try {
        const params = facetsForm.createURLParameters();
        const selectedCollectionUrl = getSelectedCollectionUrl();
        const url = appendParamsToUrl(selectedCollectionUrl || window.location.pathname, params);
        url.searchParams.set("section_id", sectionId);

        const response = await fetch(url.toString(), {
          method: "GET",
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });
        if (!response.ok) return;

        const html = await response.text();
        if (currentRequestId !== updateCountRequestId) return;

        const doc = new DOMParser().parseFromString(html, "text/html");
        const remoteApplyBtn = doc.querySelector("[data-facets-apply]");
        if (!(remoteApplyBtn instanceof HTMLButtonElement)) return;

        const nextCount = Number(remoteApplyBtn.dataset.productsCount || "0");
        setApplyButtonLabel(nextCount);
      } catch (err) {}
    };

    const scheduleApplyCountUpdate = () => {
      if (updateCountTimeout) clearTimeout(updateCountTimeout);
      setApplyButtonLoading();
      updateCountTimeout = setTimeout(() => {
        updateApplyCount();
      }, COUNT_DEBOUNCE_MS);
    };

    // Restore open state after rerender
    try {
      if (sessionStorage.getItem(storageKey) === "1") {
        wrapper.classList.add("is-open");
        trigger.setAttribute("aria-expanded", "true");
        lockBody();
      }
    } catch (e) {}

    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggle();
    });

    // Prevent any click inside wrapper from bubbling to document and closing it
    wrapper.addEventListener("click", (e) => {
      e.stopPropagation();
    });

    // Prevent closing on filter interactions (but allow Apply and Clear All buttons to work)
    panel.addEventListener("click", (e) => {
      // Don't stop propagation for Apply and Clear All buttons - they need to propagate
      if (!e.target.closest("[data-facets-apply]") && !e.target.closest(".facets__clear-all")) {
        e.stopPropagation();
      }
    });
    panel.addEventListener("click", (e) => {
      const label = e.target.closest("label[for]");
      if (!label || !panel.contains(label)) return;

      let input =
        label.closest(".checkbox")?.querySelector('input[type="checkbox"], input[type="radio"]') || null;

      if (!(input instanceof HTMLInputElement)) {
        const forId = label.getAttribute("for");
        if (!forId) return;

        const escapedId = typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(forId) : forId;
        input = panel.querySelector(`#${escapedId}`);
      }

      // Only prevent default if we found a valid input to toggle
      if (!(input instanceof HTMLInputElement)) return;
      if (input.disabled) return;
      if (input.type !== "checkbox" && input.type !== "radio") return;

      // Prevent default only after validating input exists and is usable
      e.preventDefault();
      if (input.type === "radio") input.checked = true;
      else input.checked = !input.checked;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
    panel.addEventListener("change", (e) => {
      e.stopPropagation();
      scheduleApplyCountUpdate();
    });
    panel.addEventListener("input", (e) => {
      e.stopPropagation();
      scheduleApplyCountUpdate();
    });

    // Close on outside click
    const handleOutsideClick = (e) => {
      if (!wrapper.contains(e.target)) close();
    };
    document.addEventListener("click", handleOutsideClick);
    cleanupHandlers.push(() => document.removeEventListener("click", handleOutsideClick));

    // Close on Escape
    const handleEscape = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleEscape);
    cleanupHandlers.push(() => document.removeEventListener("keydown", handleEscape));

    // Readjust position on resize if popup is open
    const handleResize = () => {
      if (isOpen()) adjustPopupPosition();
    };
    window.addEventListener("resize", handleResize);
    cleanupHandlers.push(() => window.removeEventListener("resize", handleResize));

    const closeBtn = panel.querySelector("[data-facets-popup-close]");
    if (closeBtn) {
      closeBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        close();
      });
    }

    // Handle Clear All button - ensure popup closes and body unlocks
    const clearAllBtn = panel.querySelector(".facets__clear-all");
    if (clearAllBtn) {
      clearAllBtn.addEventListener("click", () => {
        // Clear collection filter (custom filter not handled by theme)
        const collectionInputs = panel.querySelectorAll('input[name="collection-filter"]');
        collectionInputs.forEach(input => {
          if (input.checked) {
            input.checked = false;
            input.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });

        // Close popup and unlock body immediately
        close();
      }, true); // Use capture phase to run before theme's handler
    }

    const initialCount = Number(panel.querySelector("[data-facets-apply]")?.dataset.productsCount || 0);
    setApplyButtonLabel(initialCount);

    // Store cleanup function for later cleanup
    wrapper.__facetsPopupCleanup = () => {
      cleanupHandlers.forEach(fn => fn());
      if (updateCountTimeout) clearTimeout(updateCountTimeout);
    };
  }

  function mount() {
    document.querySelectorAll("[data-facets-popup]").forEach(initFacetsPopup);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount);
  } else {
    mount();
  }

  document.addEventListener("shopify:section:load", mount);
  document.addEventListener("shopify:section:reorder", mount);
  document.addEventListener("shopify:section:select", mount);
  document.addEventListener("facets:rendered", mount);

  // Cleanup on section unload to prevent memory leaks
  document.addEventListener("shopify:section:unload", (e) => {
    if (!e.target) return;
    const wrappers = e.target.querySelectorAll("[data-facets-popup]");
    wrappers.forEach((wrapper) => {
      if (typeof wrapper.__facetsPopupCleanup === "function") {
        wrapper.__facetsPopupCleanup();
        wrapper.__facetsPopupCleanup = null;
      }
    });
  });

  // Apply handler (capture to beat theme delegated handlers)
  document.addEventListener(
    "click",
    (e) => {
      const applyButton = e.target.closest("[data-facets-apply]");
      if (!applyButton) return;

      const popupPanel = applyButton.closest("[data-facets-popup-panel]");
      if (!popupPanel) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

      const facetsForm =
        popupPanel.closest("facets-form-component") || popupPanel.querySelector("facets-form-component");
      if (!facetsForm) return;

      // Collection filter is now handled as a regular filter, not a redirect
      // The collection-filter input will be included in the form data

      // Force render (popup mode normally blocks section updates)
      if (typeof facetsForm.updateFilters === "function") {
        facetsForm.updateFilters({ force: true });
      }

      // Close popup wrapper
      popupPanel.closest(".facets-popup-wrapper")?.classList.remove("is-open");
      unlockBody();

      // Close native <dialog> if present
      const dlg = applyButton.closest("dialog");
      if (dlg && typeof dlg.close === "function") {
        try {
          dlg.close();
        } catch (err) {}
      }

      // Close theme dialog-component if present
      const dialogComponent = applyButton.closest("dialog-component");
      if (dialogComponent && typeof dialogComponent.closeDialog === "function") {
        try {
          dialogComponent.closeDialog();
        } catch (err) {}
      }
    },
    true
  );
})();
